import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listScimTokens, createScimToken, revokeScimToken } from "@/lib/scim.functions";
import {
  getTenantSsoPolicy,
  setTenantSsoPolicy,
  listScimRequestLog,
  updateScimTokenSecurity,
} from "@/lib/sso-policy.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, KeyRound, ShieldCheck, Trash2, ScrollText, Settings2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/enterprise")({ component: EnterprisePage });

function EnterprisePage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const list = useServerFn(listScimTokens);
  const create = useServerFn(createScimToken);
  const revoke = useServerFn(revokeScimToken);
  const getPolicy = useServerFn(getTenantSsoPolicy);
  const setPolicy = useServerFn(setTenantSsoPolicy);
  const listLog = useServerFn(listScimRequestLog);
  const updateSecurity = useServerFn(updateScimTokenSecurity);

  const [label, setLabel] = useState("");
  const [role, setRole] = useState<"operator" | "manager" | "tenant_admin" | "viewer">("operator");
  const [shown, setShown] = useState<string | null>(null);
  const [requireSso, setRequireSso] = useState(false);
  const [ssoNotes, setSsoNotes] = useState("");
  const [securityEditing, setSecurityEditing] = useState<string | null>(null);
  const [ipCidrs, setIpCidrs] = useState("");
  const [rateLimit, setRateLimit] = useState(120);

  const { data } = useQuery({
    queryKey: ["scim-tokens", tenantId],
    enabled: !!tenantId,
    queryFn: () => list({ data: { tenantId: tenantId! } }),
  });

  const { data: policy } = useQuery({
    queryKey: ["sso-policy", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const res = await getPolicy({ data: { tenantId: tenantId! } });
      setRequireSso(res.requireSso);
      setSsoNotes(res.ssoNotes);
      return res;
    },
  });

  const { data: log } = useQuery({
    queryKey: ["scim-log", tenantId],
    enabled: !!tenantId,
    queryFn: () => listLog({ data: { tenantId: tenantId!, limit: 100 } }),
    refetchInterval: 30_000,
  });

  const onCreate = async () => {
    if (!tenantId || !label) { toast.error("Label required"); return; }
    const res = await runServerFn(() => create({ data: { tenantId, label, defaultRole: role } }), "Create SCIM token");
    setShown(res.token);
    setLabel("");
    qc.invalidateQueries({ queryKey: ["scim-tokens"] });
  };
  const onRevoke = async (id: string) => {
    if (!confirm("Revoke this token? IdP will lose access immediately.")) return;
    await runServerFn(() => revoke({ data: { id } }), "Revoke SCIM token");
    qc.invalidateQueries({ queryKey: ["scim-tokens"] });
  };
  const onSavePolicy = async () => {
    if (!tenantId) return;
    await runServerFn(
      () => setPolicy({ data: { tenantId, requireSso, ssoNotes } }),
      "Update SSO policy",
    );
    qc.invalidateQueries({ queryKey: ["sso-policy"] });
  };
  const onOpenSecurity = (t: any) => {
    setSecurityEditing(t.id);
    setIpCidrs((t.allowed_ip_cidrs ?? []).join(", "));
    setRateLimit(t.rate_limit_per_minute ?? 120);
  };
  const onSaveSecurity = async () => {
    if (!tenantId || !securityEditing) return;
    const cidrs = ipCidrs
      .split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    await runServerFn(
      () => updateSecurity({ data: {
        id: securityEditing, tenantId,
        allowedIpCidrs: cidrs, rateLimitPerMinute: rateLimit,
      } }),
      "Update token security",
    );
    setSecurityEditing(null);
    qc.invalidateQueries({ queryKey: ["scim-tokens"] });
  };
  const onCopy = (s: string) => { navigator.clipboard.writeText(s); toast.success("Copied"); };

  const scimUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/api/public/scim/v2`;

  return (
    <>
      <PageHeader title="Enterprise SSO & SCIM" description="SAML 2.0 single sign-on and SCIM 2.0 user provisioning for enterprise tenants." />

      <div className="px-4 sm:px-6 py-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> SAML SSO</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>SAML 2.0 federates sign-in to your IdP (Okta, Entra ID, OneLogin). Configure via Lovable Cloud → Authentication.</p>
            <div className="space-y-2 text-xs">
              <div><span className="text-muted-foreground">ACS URL:</span> <span className="font-mono break-all">https://lirsvorlpfwishgjrmnc.supabase.co/auth/v1/sso/saml/acs</span></div>
              <div><span className="text-muted-foreground">Entity ID:</span> <span className="font-mono break-all">https://lirsvorlpfwishgjrmnc.supabase.co/auth/v1/sso/saml/metadata</span></div>
            </div>
            <p className="text-xs text-muted-foreground">Provide your IdP metadata URL and email domains to your Lovable admin to enable.</p>
            <div className="border-t pt-3 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">Require SSO for this tenant</div>
                  <div className="text-xs text-muted-foreground">When on, password sign-in is blocked at /login for users in your domains.</div>
                </div>
                <Switch checked={requireSso} onCheckedChange={setRequireSso} />
              </div>
              <Label className="text-xs">Notes (shown to users in support)</Label>
              <Textarea
                value={ssoNotes}
                onChange={(e) => setSsoNotes(e.target.value)}
                placeholder="e.g. Sign in via Okta tile WMS-Prod"
                rows={2}
              />
              <Button size="sm" onClick={onSavePolicy} disabled={!policy}>Save policy</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" /> SCIM 2.0 endpoint</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="text-xs">
              <span className="text-muted-foreground">Base URL:</span>{" "}
              <span className="font-mono break-all">{scimUrl}</span>
              <Button size="sm" variant="ghost" className="h-6 ml-1 px-1" onClick={() => onCopy(scimUrl)}><Copy className="h-3 w-3" /></Button>
            </div>
            <p className="text-xs text-muted-foreground">Supports <code>GET/POST /Users</code>, <code>GET/PUT/DELETE /Users/{`{id}`}</code>. Bearer token auth.</p>

            <div className="border-t pt-3 space-y-2">
              <Label className="text-xs">Issue new bearer token</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Label (e.g. Okta-prod)" value={label} onChange={(e) => setLabel(e.target.value)} />
                <Select value={role} onValueChange={(v) => setRole(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">Operator</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="tenant_admin">Tenant admin</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={onCreate}>Generate token</Button>
            </div>

            {shown && (
              <div className="rounded border bg-warning/10 p-3 space-y-2">
                <div className="text-xs font-medium text-warning">Copy now — won't be shown again.</div>
                <div className="font-mono text-xs break-all bg-background p-2 rounded">{shown}</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => onCopy(shown)}><Copy className="h-3 w-3 mr-1" /> Copy</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShown(null)}>Done</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="px-4 sm:px-6 pb-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Active Tokens</CardTitle></CardHeader>
          <CardContent className="p-0">
            {(data?.tokens ?? []).length === 0 ? (
              <div className="p-6 text-sm text-center text-muted-foreground">No tokens issued.</div>
            ) : (
              <ul className="divide-y">
                {data!.tokens.map((t: any) => (
                  <li key={t.id} className="p-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{t.label}</div>
                      <div className="text-xs text-muted-foreground font-mono">{t.token_prefix}…  ·  role: {t.default_role}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {t.last_used_at ? `last used ${new Date(t.last_used_at).toLocaleString()}` : "never used"}
                        {" · "}rate {t.rate_limit_per_minute ?? 120}/min
                        {(t.allowed_ip_cidrs?.length ?? 0) > 0
                          ? ` · ${t.allowed_ip_cidrs.length} IP rule(s)`
                          : " · any IP"}
                      </div>
                      {securityEditing === t.id && (
                        <div className="mt-2 space-y-2 rounded border p-2 bg-muted/30">
                          <div className="space-y-1">
                            <Label className="text-xs">Allowed IP CIDRs (comma-separated, IPv4)</Label>
                            <Input value={ipCidrs} onChange={(e) => setIpCidrs(e.target.value)} placeholder="203.0.113.0/24, 198.51.100.7" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Rate limit (requests / minute)</Label>
                            <Input type="number" min={1} max={10000} value={rateLimit} onChange={(e) => setRateLimit(Number(e.target.value) || 120)} />
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={onSaveSecurity}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setSecurityEditing(null)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {t.revoked_at ? (
                        <Badge variant="outline" className="text-muted-foreground">revoked</Badge>
                      ) : (
                        <>
                        <Button size="sm" variant="ghost" onClick={() => onOpenSecurity(t)} title="Edit security">
                          <Settings2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onRevoke(t.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><ScrollText className="h-4 w-4" /> SCIM request log</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(log?.entries ?? []).length === 0 ? (
              <div className="p-6 text-sm text-center text-muted-foreground">No SCIM activity recorded yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-muted-foreground">
                    <tr className="text-left">
                      <th className="p-2">When</th>
                      <th className="p-2">Method</th>
                      <th className="p-2">Path</th>
                      <th className="p-2">Status</th>
                      <th className="p-2">IP</th>
                      <th className="p-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log!.entries.map((e: any) => (
                      <tr key={e.id} className="border-t">
                        <td className="p-2 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                        <td className="p-2 font-mono">{e.method}</td>
                        <td className="p-2 font-mono break-all">{e.path}</td>
                        <td className="p-2">
                          <Badge variant={e.status >= 400 ? "destructive" : "outline"}>{e.status}</Badge>
                        </td>
                        <td className="p-2 font-mono">{e.remote_ip ?? "—"}</td>
                        <td className="p-2">{e.duration_ms ?? 0} ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
